import * as React from "react"
import { Box, Center, Spinner, Tabs, TabList, Tab, TabPanels, TabPanel } from "@chakra-ui/react"
import jwtDecode from "jwt-decode"
import { useNavigate } from "react-router-dom"
import { useOrgs } from "../components/providers/OrgProvider"
import { AUTH_TOKEN, IS_TEST } from "../lib/config"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"
import { refetchToken } from "../lib/fetcher"
import {
  APPLE_DATASTUDIO_URL,
  GOOGLE_DATASTUDIO_URL,
  ORGS_HAS_DASHBOARD,
  ORGS_IS_APPLE,
  ORGS_IS_GOOGLE,
} from "../lib/static/organisation"

const DashboardIframe = ({ dashboardUrl }: { dashboardUrl: string }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(true)
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  React.useEffect(() => {
    if (!iframeRef.current) return
    iframeRef.current.onload = () => {
      setLoading(false)
    }
    const handleMessage = async (event: MessageEvent) => {
      try {
        if (!iframeRef.current) return
        if (event.origin !== "https://iris.noa.one" || event.data !== "update-jwt") return

        let jwt = localStorage.getItem(AUTH_TOKEN)
        if (!jwt) return
        const decoded = jwtDecode<{ exp: number }>(jwt)
        if (!decoded) return

        const now = new Date().getTime()
        if (decoded.exp + 60000 >= now) {
          // refresh the JWT if it's expired or due to expire in the next minute
          const newPayload = await refetchToken()
          jwt = newPayload.token
        }

        iframeRef.current.contentWindow?.postMessage({ jwt }, event.origin)
      } catch {
        navigate("/")
      }
    }
    window.addEventListener("message", handleMessage)
    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [navigate])

  return (
    <Loader loading={loading}>
      <Box as="iframe" w="100%" h="100%" title="dashboard" src={dashboardUrl} ref={iframeRef} />
    </Loader>
  )
}

export const Dashboard = () => {
  useDocumentTitle("Dashboard")
  const { currentOrgs } = useOrgs()

  if (!ORGS_HAS_DASHBOARD(currentOrgs)) return null

  const dashboardTabs = currentOrgs
    .filter((org) => ORGS_HAS_DASHBOARD([org]) && !ORGS_IS_APPLE([org]) && !ORGS_IS_GOOGLE([org]))
    .map((org) => {
      return {
        tabTitle: org.name,
        dashboardUrl: `https://iris.noa.one/?org_uuid=${org.uuid}&environment=${
          IS_TEST ? "testing" : "production"
        }`,
      }
    })

  if (ORGS_IS_APPLE(currentOrgs))
    dashboardTabs.splice(0, 0, {
      tabTitle: "Apple",
      dashboardUrl: APPLE_DATASTUDIO_URL,
    })

  if (ORGS_IS_GOOGLE(currentOrgs))
    dashboardTabs.splice(0, 0, {
      tabTitle: "Google",
      dashboardUrl: GOOGLE_DATASTUDIO_URL,
    })

  if (dashboardTabs.length > 1) {
    return (
      <Tabs isLazy h="100vh">
        <TabList>
          {dashboardTabs.map(({ tabTitle }: { tabTitle: string }, index) => {
            return (
              <Tab key={index} _focus={{ outline: "none" }}>
                {tabTitle}
              </Tab>
            )
          })}
        </TabList>

        <TabPanels h="calc(100% - 42px)">
          {dashboardTabs.map(({ dashboardUrl }: { dashboardUrl: string }, index) => {
            return (
              <TabPanel key={index} p={0} position="relative" h="100%">
                <DashboardIframe dashboardUrl={dashboardUrl} />
              </TabPanel>
            )
          })}
        </TabPanels>
      </Tabs>
    )
  }

  return (
    <Box h="100vh">
      <DashboardIframe dashboardUrl={dashboardTabs[0].dashboardUrl} />
    </Box>
  )
}

function Loader(props: { children: React.ReactNode; loading: boolean }) {
  return (
    <>
      {props.children}
      {props.loading && (
        <Center pos="absolute" top={0} left={0} h="100%" w="100%" bg="white">
          <Spinner />
        </Center>
      )}
    </>
  )
}
